/**
 * Date and time utility functions
 */

/**
 * Extract date components from a Date object with zero-padding
 * 
 * @param date - Date object to extract components from
 * @returns Object with formatted date components (year, month, day, hours, minutes, seconds)
 */
function extractDateComponents(date: Date) {
  return {
    year: date.getFullYear().toString(),
    month: (date.getMonth() + 1).toString().padStart(2, '0'),
    day: date.getDate().toString().padStart(2, '0'),
    hours: date.getHours().toString().padStart(2, '0'),
    minutes: date.getMinutes().toString().padStart(2, '0'),
    seconds: date.getSeconds().toString().padStart(2, '0'),
  };
}

/**
 * Format date components to MM/DD HH:MM format
 * 
 * @param components - Date components object
 * @returns Formatted date string in MM/DD HH:MM format
 */
function formatDateTime(components: ReturnType<typeof extractDateComponents>): string {
  return `${components.month}/${components.day} ${components.hours}:${components.minutes}`;
}

/**
 * Format Unix timestamp (milliseconds) to MM/DD HH:MM format
 * Example: 1738033305000 -> "01/27 18:01"
 * 
 * @param timestamp - Unix timestamp in milliseconds
 * @returns Formatted date string in MM/DD HH:MM format
 */
export function formatUnixTimestampToDateTime(timestamp: number): string {
  const date = new Date(timestamp);
  const components = extractDateComponents(date);
  return formatDateTime(components);
}

/**
 * Format ISO timestamp string to MM/DD HH:MM format in local time
 * Example: "2026-01-30T05:12:25.950735Z" -> "01/29 23:12" (CST)
 * 
 * @param isoTimestamp - ISO timestamp string from backend
 * @returns Formatted date string in MM/DD HH:MM format (local time)
 */
export function formatISOTimestampToDateTime(isoTimestamp: string): string {
  const date = new Date(isoTimestamp);
  const components = extractDateComponents(date);
  return formatDateTime(components);
}
